import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';

import { directoryKeys } from '@/shared/api/directory';
import { api } from '@/shared/api/http';
import type {
  GroupListItem as SharedGroupListItem,
  GroupRole,
  GroupVisibility,
  IsoDateTime,
  PagedResult,
  UserRef,
} from '@/shared/api/types';

import { isGroupDeleted } from './lib/deleted';

/*
 * Groups API (backend module `groups`, route root /api/groups — docs/modules/groups.md).
 * Query keys start with the module id `groups`; the shared picker's `['groups', 'search', …]` keys live under the same
 * root, so `invalidateQueries({ queryKey: groupKeys.all })` refreshes the pickers too.
 */

export type { GroupRole, GroupVisibility };

/** ISO date without time (`2026-10-09`). */
export type IsoDate = string;

/** List item (§4.10 contract fields + the extras the groups pages need). */
export interface GroupListItem extends SharedGroupListItem {
  isArchived: boolean;
  projectStart: IsoDate | null;
  projectEnd: IsoDate | null;
  memberPreview: UserRef[];
  hasPendingRequest: boolean;
  hasPendingInvitation: boolean;
  /** With `userId=`: that user's role. */
  userRole: GroupRole | null;
  createdAt: IsoDateTime;
}

export interface GroupPermissions {
  canView: boolean;
  canViewContent: boolean;
  canEdit: boolean;
  canArchive: boolean;
  canDelete: boolean;
  canInvite: boolean;
  canManageRequests: boolean;
  canRemoveMembers: boolean;
  canChangeRoles: boolean;
  canTransferOwnership: boolean;
  canJoin: boolean;
  canRequestToJoin: boolean;
  canCancelRequest: boolean;
  canRespondToInvitation: boolean;
  canLeave: boolean;
}

export interface MyJoinRequest {
  id: string;
  message: string | null;
  createdAt: IsoDateTime;
}

export interface MyInvitation {
  id: string;
  invitedBy: UserRef | null;
  message: string | null;
  createdAt: IsoDateTime;
}

export interface GroupDetails {
  id: string;
  name: string;
  description: string | null;
  descriptionHtml: string;
  avatarUrl: string | null;
  isProject: boolean;
  visibility: GroupVisibility;
  projectStart: IsoDate | null;
  projectEnd: IsoDate | null;
  isArchived: boolean;
  archivedAt: IsoDateTime | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  owner: UserRef | null;
  memberCount: number;
  moderatorCount: number;
  memberPreview: UserRef[];
  myRole: GroupRole | null;
  myJoinRequest: MyJoinRequest | null;
  myInvitation: MyInvitation | null;
  pendingRequestCount: number;
  pendingInvitationCount: number;
  permissions: GroupPermissions;
  version: number;
}

export interface GroupMember {
  user: UserRef;
  role: GroupRole;
  joinedAt: IsoDateTime;
}

export interface JoinRequest {
  id: string;
  groupId: string;
  user: UserRef;
  message: string | null;
  createdAt: IsoDateTime;
}

export interface GroupInvitation {
  id: string;
  groupId: string;
  user: UserRef;
  invitedBy: UserRef | null;
  message: string | null;
  createdAt: IsoDateTime;
}

export interface MyGroupInvitation {
  id: string;
  group: GroupListItem;
  invitedBy: UserRef | null;
  message: string | null;
  createdAt: IsoDateTime;
}

export interface GroupCounters {
  invitations: number;
  joinRequests: number;
}

export interface InviteResult {
  invited: GroupInvitation[];
  added: string[];
  alreadyMembers: string[];
  alreadyInvited: string[];
}

export interface MyRole {
  role: GroupRole | null;
  canViewContent: boolean;
}

export type GroupSort = 'name' | 'newest' | 'members';

export interface GroupListParams {
  search?: string;
  mine?: boolean;
  isProject?: boolean;
  archived?: boolean;
  userId?: string;
  sort?: GroupSort;
  page?: number;
  pageSize?: number;
}

export interface GroupSettingsInput {
  name: string;
  descriptionHtml: string | null;
  isProject: boolean;
  visibility: GroupVisibility;
  projectStart: IsoDate | null;
  projectEnd: IsoDate | null;
}

export interface CreateGroupInput extends GroupSettingsInput {
  avatarFileId: string | null;
  inviteUserIds: string[];
}

export interface UpdateGroupInput extends GroupSettingsInput {
  version?: number;
}

export interface MemberListParams {
  search?: string;
  role?: GroupRole;
  page?: number;
  pageSize?: number;
}

export const groupKeys = {
  all: directoryKeys.groups,
  lists: () => [...directoryKeys.groups, 'list'] as const,
  list: (params: GroupListParams) => [...directoryKeys.groups, 'list', params] as const,
  details: () => [...directoryKeys.groups, 'detail'] as const,
  detail: (id: string) => [...directoryKeys.groups, 'detail', id] as const,
  members: (id: string) => [...directoryKeys.groups, 'members', id] as const,
  memberList: (id: string, params: MemberListParams) =>
    [...directoryKeys.groups, 'members', id, params] as const,
  joinRequests: (id: string) => [...directoryKeys.groups, 'join-requests', id] as const,
  invitations: (id: string) => [...directoryKeys.groups, 'invitations', id] as const,
  myInvitations: [...directoryKeys.groups, 'me', 'invitations'] as const,
  counters: [...directoryKeys.groups, 'me', 'counters'] as const,
  myRole: (id: string) => [...directoryKeys.groups, 'my-role', id] as const,
};

const path = (id: string, rest = '') => `/groups/${encodeURIComponent(id)}${rest}`;

export const groupsApi = {
  list: (params: GroupListParams, signal?: AbortSignal) =>
    api.get<PagedResult<GroupListItem>>('/groups', { query: { ...params }, signal }),
  get: (id: string, signal?: AbortSignal) => api.get<GroupDetails>(path(id), { signal }),
  myRole: (id: string, signal?: AbortSignal) => api.get<MyRole>(path(id, '/my-role'), { signal }),
  create: (input: CreateGroupInput) => api.post<GroupDetails>('/groups', input),
  update: (id: string, input: UpdateGroupInput) => api.put<GroupDetails>(path(id), input),
  setAvatar: (id: string, fileId: string) => api.put<GroupDetails>(path(id, '/avatar'), { fileId }),
  removeAvatar: (id: string) => api.delete<GroupDetails>(path(id, '/avatar')),
  archive: (id: string) => api.post<GroupDetails>(path(id, '/archive')),
  unarchive: (id: string) => api.post<GroupDetails>(path(id, '/unarchive')),
  remove: (id: string) => api.delete(path(id)),
  members: (id: string, params: MemberListParams, signal?: AbortSignal) =>
    api.get<PagedResult<GroupMember>>(path(id, '/members'), { query: { ...params }, signal }),
  join: (id: string) => api.post<GroupDetails>(path(id, '/join')),
  leave: (id: string) => api.post(path(id, '/leave')),
  removeMember: (id: string, userId: string) =>
    api.delete(path(id, `/members/${encodeURIComponent(userId)}`)),
  changeRole: (id: string, userId: string, role: Exclude<GroupRole, 'owner'>) =>
    api.put<GroupMember>(path(id, `/members/${encodeURIComponent(userId)}/role`), { role }),
  transferOwnership: (id: string, userId: string) => api.post<GroupDetails>(path(id, '/owner'), { userId }),
  invitations: (id: string, signal?: AbortSignal) =>
    api.get<GroupInvitation[]>(path(id, '/invitations'), { signal }),
  invite: (id: string, userIds: string[], message?: string | null) =>
    api.post<InviteResult>(path(id, '/invitations'), { userIds, message: message?.length ? message : null }),
  cancelInvitation: (id: string, invitationId: string) =>
    api.delete(path(id, `/invitations/${encodeURIComponent(invitationId)}`)),
  acceptInvitation: (id: string) => api.post<GroupDetails>(path(id, '/invitation/accept')),
  declineInvitation: (id: string) => api.post(path(id, '/invitation/decline')),
  joinRequests: (id: string, signal?: AbortSignal) =>
    api.get<JoinRequest[]>(path(id, '/join-requests'), { signal }),
  requestToJoin: (id: string, message?: string | null) =>
    api.post<JoinRequest>(path(id, '/join-requests'), { message: message?.length ? message : null }),
  cancelJoinRequest: (id: string) => api.delete(path(id, '/join-requests/mine')),
  approveRequest: (id: string, requestId: string) =>
    api.post(path(id, `/join-requests/${encodeURIComponent(requestId)}/approve`)),
  declineRequest: (id: string, requestId: string) =>
    api.post(path(id, `/join-requests/${encodeURIComponent(requestId)}/decline`)),
  myInvitations: (signal?: AbortSignal) => api.get<MyGroupInvitation[]>('/groups/me/invitations', { signal }),
  counters: (signal?: AbortSignal) => api.get<GroupCounters>('/groups/me/counters', { signal }),
};

// ---- queries ------------------------------------------------------------------------------------

export function useGroupList(params: GroupListParams, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: groupKeys.list(params),
    queryFn: ({ signal }) => groupsApi.list(params, signal),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

export function useGroup(id: string | null | undefined) {
  return useQuery({
    queryKey: groupKeys.detail(id ?? ''),
    queryFn: ({ signal }) => groupsApi.get(id ?? '', signal),
    // Checked whenever a refetch is considered, not only when this hook renders: while the page of a group just deleted is
    // still on screen (the route changes a moment later, longer on a slow device), nothing may refetch it — that is a 404.
    enabled: () => !!id && !isGroupDeleted(id),
  });
}

export function useGroupMembers(id: string, params: MemberListParams, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: groupKeys.memberList(id, params),
    queryFn: ({ signal }) => groupsApi.members(id, params, signal),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

export function useJoinRequests(id: string, enabled: boolean) {
  return useQuery({
    queryKey: groupKeys.joinRequests(id),
    queryFn: ({ signal }) => groupsApi.joinRequests(id, signal),
    enabled,
  });
}

export function useGroupInvitations(id: string, enabled: boolean) {
  return useQuery({
    queryKey: groupKeys.invitations(id),
    queryFn: ({ signal }) => groupsApi.invitations(id, signal),
    enabled,
  });
}

export function useMyInvitations() {
  return useQuery({
    queryKey: groupKeys.myInvitations,
    queryFn: ({ signal }) => groupsApi.myInvitations(signal),
  });
}

export function useGroupCounters(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: groupKeys.counters,
    queryFn: ({ signal }) => groupsApi.counters(signal),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

// ---- mutations ----------------------------------------------------------------------------------

/** Everything a membership or settings change can affect (lists, pickers, counters, the group's queries). */
export async function invalidateGroup(queryClient: QueryClient, id?: string): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: groupKeys.lists() }),
    queryClient.invalidateQueries({ queryKey: [...groupKeys.all, 'search'] }),
    queryClient.invalidateQueries({ queryKey: groupKeys.counters }),
    queryClient.invalidateQueries({ queryKey: groupKeys.myInvitations }),
    ...(id
      ? [
          queryClient.invalidateQueries({ queryKey: groupKeys.detail(id) }),
          queryClient.invalidateQueries({ queryKey: groupKeys.members(id) }),
          queryClient.invalidateQueries({ queryKey: groupKeys.joinRequests(id) }),
          queryClient.invalidateQueries({ queryKey: groupKeys.invitations(id) }),
          queryClient.invalidateQueries({ queryKey: groupKeys.myRole(id) }),
        ]
      : []),
  ]);
}

/** A mutation that refreshes the group's queries afterwards (and stores returned details right away). */
export function useGroupMutation<TVariables, TResult>(
  groupId: string,
  mutationFn: (variables: TVariables) => Promise<TResult>,
  options: { successMessage?: string; silent?: boolean } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    meta: { successMessage: options.successMessage, silent: options.silent },
    onSuccess: (result) => {
      if (isGroupDetails(result)) queryClient.setQueryData(groupKeys.detail(result.id), result);
    },
    onSettled: () => invalidateGroup(queryClient, groupId),
  });
}

export function isGroupDetails(value: unknown): value is GroupDetails {
  return typeof value === 'object' && value !== null && 'permissions' in value && 'memberPreview' in value;
}

type ListData = PagedResult<GroupListItem> | InfiniteData<PagedResult<GroupListItem>>;

/** Applies `patch` to the group in a cached list page (plain or infinite query data). */
export function patchListData(
  data: ListData | undefined,
  groupId: string,
  patch: Partial<GroupListItem>,
): ListData | undefined {
  if (!data) return data;
  const patchPage = (page: PagedResult<GroupListItem>) => ({
    ...page,
    items: page.items.map((g) => (g.id === groupId ? { ...g, ...patch } : g)),
  });
  return 'pages' in data ? { ...data, pages: data.pages.map(patchPage) } : patchPage(data);
}

/** Optimistically patches the group in every cached list (join / request buttons on cards); returns the rollback. */
export function patchListItem(
  queryClient: QueryClient,
  groupId: string,
  patch: Partial<GroupListItem>,
): () => void {
  const previous = queryClient.getQueriesData<ListData>({ queryKey: groupKeys.lists() });
  queryClient.setQueriesData<ListData>({ queryKey: groupKeys.lists() }, (data) =>
    patchListData(data, groupId, patch),
  );
  return () => {
    for (const [key, data] of previous) queryClient.setQueryData(key, data);
  };
}
